import type { SimpleSelectOptions } from "@/modules/uiKitOverrides/SelectFilter/Select"

import { useEffect, useState } from "react"
import { NIL as NIL_UUID } from "uuid"

import {
  generateOptionsFromSearchConfigAndFacets,
  getInitialOptions,
} from "@/modules/jobAds/client/utils/generateOptions"
import { usePageContext } from "@/modules/jobSearch/components/PageContext/PageContextProvider"

import { useJobAdsSearchParams } from "./useJobAdsSearchParams"

export type SelectedCcFiltersParam = "alumniJobs" | "facultyPartnerJobs" | "partnerJobs" | "targetedJobs"

export type SelectedCcFiltersParamValue =
  | "created_by_student_or_alumni"
  | "partner_career_center"
  | "partner_faculty_career_center"
  | "reserved"

export type SelectedCcFilters = Record<SelectedCcFiltersParam, boolean>

export type SelectedTagsFilter = Record<"tags", SimpleSelectOptions[]>

const appliedCcFiltersValues: {
  readonly [K in SelectedCcFiltersParam]: SelectedCcFiltersParamValue
} = {
  alumniJobs: "created_by_student_or_alumni",
  facultyPartnerJobs: "partner_faculty_career_center",
  partnerJobs: "partner_career_center",
  targetedJobs: "reserved",
}

const emptyCcFilters: SelectedCcFilters = {
  alumniJobs: false,
  facultyPartnerJobs: false,
  partnerJobs: false,
  targetedJobs: false,
}

const emptyTagsFilter: SelectedTagsFilter = {
  tags: [],
}

type UseCcFilters = {
  getCcParameters: (newCcFilters: SelectedCcFilters) => { exclusive: string[] }
  getTagParameter: (newTagsFilter: SelectedTagsFilter) => { tags: string[] }
  selectedCcFiltersNumber: number
  ccFilters: SelectedCcFilters
  tagsFilter: SelectedTagsFilter
}

export const useCcFilters = (): UseCcFilters => {
  const { careerCenterConf, careerCenterId, careerCenterMainId, currentUserSearchConfig } = usePageContext()
  const isCareerCenterAFaculty = Boolean(careerCenterMainId)
  const isCareerCenter = careerCenterId !== NIL_UUID

  const [ccFilters, setCcFilters] = useState(emptyCcFilters)
  const [tagsFilter, setTagsFilter] = useState(emptyTagsFilter)
  const [selectedCcFiltersNumber, setSelectedCcFiltersNumber] = useState(0)

  const searchParams = useJobAdsSearchParams()
  const exclusiveQuery = searchParams.getAll("exclusive")
  const tagsQuery = searchParams.getAll("tags")

  useEffect(() => {
    if (isCareerCenter) {
      const options = generateOptionsFromSearchConfigAndFacets({
        facets: [],
        filterNameFromFacet: "tags",
        filterNameFromSearchConfig: "tags",
        searchConfig: currentUserSearchConfig?.searchFilters || [],
      })
      setTagsFilter({ tags: getInitialOptions({ options, param: tagsQuery }) })
    }
    // The tagsQuery value changes on every render so we only check its pure value
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [careerCenterConf, currentUserSearchConfig, isCareerCenter, tagsQuery.join("_")])

  useEffect(() => {
    if (isCareerCenter) {
      const ccFilterValue = exclusiveQuery

      const appliedSelectedCcFilters: SelectedCcFilters = { ...emptyCcFilters }

      if (ccFilterValue) {
        Object.entries(appliedCcFiltersValues).forEach(ccFilter => {
          const ccFilterName = ccFilter[0] as SelectedCcFiltersParam
          const ccFilterAssociatedParamValue = ccFilter[1]
          const isCCFilterSelected = ccFilterValue.includes(ccFilterAssociatedParamValue)

          appliedSelectedCcFilters[ccFilterName] = isCCFilterSelected
        })
      }
      setCcFilters(appliedSelectedCcFilters)
    }
    // The exclusiveQuery value changes on every render so we only check its pure value
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exclusiveQuery.join("_"), isCareerCenter])

  useEffect(() => {
    setSelectedCcFiltersNumber(
      Object.entries(ccFilters)
        .filter(([key]) => isCareerCenterAFaculty || key !== "facultyPartnerJobs")
        .filter(([_key, value]) => Boolean(value)).length + tagsFilter.tags.length
    )
  }, [ccFilters, tagsFilter.tags.length, isCareerCenterAFaculty])

  const getCcParameters = (newCcFilters: SelectedCcFilters): { exclusive: string[] } => {
    const ccFiltersArray = Object.entries(newCcFilters) as [SelectedCcFiltersParam, boolean][]
    return {
      exclusive: ccFiltersArray
        .filter(keyValuePair => keyValuePair[1])
        .map(keyValuePair => {
          const filterKey = keyValuePair[0]
          return appliedCcFiltersValues[filterKey]
        }),
    }
  }

  const getTagsParameter = (newTagsFilter: SelectedTagsFilter): { tags: string[] } => ({
    tags: newTagsFilter.tags.map(tag => tag.value),
  })

  return {
    ccFilters,
    getCcParameters,
    getTagParameter: getTagsParameter,
    selectedCcFiltersNumber,
    tagsFilter,
  }
}
